import type { MutatingScreenKind } from "@glide/app-description";
import {
    ArrayTransformKind,
    getContentTransforms,
    ScreenDescriptionKind,
    type ClassScreenDescription,
    type ComponentDescription,
    type PropertyDescription,
} from "@glide/app-description";
import { ErrorInfo, isErrorInfo, type ErrorOr } from "@glide/error-info";
import { makeTableName, makeTableRef, type Description } from "@glide/type-schema";
import { screenTitlePropertyDescriptor } from "./components/descriptor-utils";
import {
    getMutatingKindForScreen,
    getPropertyDescription,
    isMultiCasePropertyDescriptor,
    updatesForProperty,
    type AppDescriptionContext,
    type PropertyDescriptor,
} from "@glide/function-utils";
import { hasOwnProperty, mapFilterUndefined } from "@glideapps/ts-necessities";
import { handlerForPropertyKind } from "./components/description-handlers";
import { handlerForComponentKind } from "./handlers";
import { getSubComponentsWithTables } from "./sub-components";
import { inputOutputTablesForClassOrArrayScreen } from "./description-utils";
import type { InputOutputTables } from "@glide/common-core/dist/js/description";

function normalizePropertyDescription(descr: PropertyDescriptor, value: unknown): PropertyDescription | undefined {
    if (isMultiCasePropertyDescriptor(descr)) {
        for (const pdc of descr.cases) {
            const handler = handlerForPropertyKind(pdc.kind);
            const normalized = handler.normalizeDescription(pdc, value);
            if (normalized === undefined) continue;
            return normalized;
        }
        return undefined;
    } else {
        const handler = handlerForPropertyKind(descr.kind);
        return handler.normalizeDescription(descr, value);
    }
}

function isTransformsPropertyDescriptor(descr: PropertyDescriptor): boolean {
    if (isMultiCasePropertyDescriptor(descr) || !hasOwnProperty(descr.property, "name")) return false;

    return descr.property.name === "transforms";
}

export function normalizeDescription(
    propertyDescriptors: readonly PropertyDescriptor[],
    description: Description
): Description {
    let normalizedDescription: Description = {};

    for (const pd of propertyDescriptors) {
        // There's an special case for transforms property. So we handle this here.
        if (isTransformsPropertyDescriptor(pd)) {
            const transforms = getContentTransforms(description);
            normalizedDescription = {
                ...normalizedDescription,
                transforms,
            };
            continue;
        }
        const desc = getPropertyDescription(description, pd);
        if (desc === undefined) continue;
        const normalized = normalizePropertyDescription(pd, desc);
        if (normalized === undefined) continue;

        normalizedDescription = {
            ...normalizedDescription,
            ...updatesForProperty(pd.property, normalizedDescription, normalized, undefined),
        };
    }

    return normalizedDescription;
}

function normalizeComponentDescription(
    adc: AppDescriptionContext,
    component: ComponentDescription,
    tables: InputOutputTables | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    isFirstComponent: boolean
): ErrorOr<ComponentDescription> {
    const handler = handlerForComponentKind(component.kind);
    if (handler === undefined) return new ErrorInfo(400, `Unknown component kind: ${component.kind}`);

    const componentDescriptor = handler.getDescriptor(
        component,
        tables,
        adc,
        mutatingScreenKind,
        false,
        isFirstComponent
    );

    const propertyDescriptors = [
        ...(componentDescriptor.additionalNormalizationProperties ?? []),
        ...componentDescriptor.properties,
    ];

    let normalizedComponent: ComponentDescription = {
        ...normalizeDescription(propertyDescriptors, component),

        kind: component.kind,
        componentID: component.componentID,

        visibilityFilters: component.visibilityFilters?.filter(t => t.kind === ArrayTransformKind.Filter) ?? [],
    };

    const maybeSubComponents = getSubComponentsWithTables(component, undefined, adc, undefined);
    if (maybeSubComponents !== undefined) {
        const [subComponents, subComponentTables, subComponentMutatingScreenKind] = maybeSubComponents;
        const normalizedSubComponents = mapFilterUndefined(subComponents, desc => {
            const normalized = normalizeComponentDescription(
                adc,
                desc,
                subComponentTables,
                subComponentMutatingScreenKind,
                false
            );
            if (isErrorInfo(normalized)) return undefined;
            return normalized;
        });
        normalizedComponent = { components: normalizedSubComponents, ...normalizedComponent } as ComponentDescription;
    }

    return normalizedComponent;
}

export function normalizeScreenDescription(
    adc: AppDescriptionContext,
    screenName: string
): ErrorOr<ClassScreenDescription> {
    const screenDescription = adc.appDescription?.screenDescriptions[screenName];
    if (screenDescription === undefined) {
        return new ErrorInfo(400, "Screen not found");
    }
    if (screenDescription?.kind !== ScreenDescriptionKind.Class) {
        return new ErrorInfo(400, "Screen is not a class screen");
    }
    // FIXME: support these
    if (screenDescription.isForm === true) return new ErrorInfo(400, "Form screens are not supported yet");

    const tables = inputOutputTablesForClassOrArrayScreen(screenDescription, adc.schema);
    const mutatingScreenKind = getMutatingKindForScreen(screenName, screenDescription);

    return {
        kind: ScreenDescriptionKind.Class,
        type: makeTableRef(makeTableName(screenDescription.type.tableName)),
        isForm: false,
        fetchesData: screenDescription.fetchesData ?? false,
        title: normalizePropertyDescription(screenTitlePropertyDescriptor, screenDescription.title),
        components: mapFilterUndefined(screenDescription.components, (desc, i) => {
            const normalized = normalizeComponentDescription(adc, desc, tables, mutatingScreenKind, i === 0);
            if (isErrorInfo(normalized)) return undefined;
            return normalized;
        }),
        transforms: screenDescription.transforms ?? [],
    };
}
